import { Chip, Paper, TextField, Button, Dialog, DialogTitle, DialogContent, IconButton } from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import Autocomplete from "@material-ui/lab/Autocomplete";
import React, { useContext, useEffect, useRef, useState } from "react";
import { isArray, isString } from "lodash";
import toastError from "../../errors/toastError";
import api from "../../services/api";
import { AuthContext } from "../../context/Auth/AuthContext";

export function TagsContainer({ contact }) {
    const { user } = useContext(AuthContext);
    const [tags, setTags] = useState([]);
    const [selecteds, setSelecteds] = useState([]);
    const [allContactTags, setAllContactTags] = useState([]);
    const [showAllTags, setShowAllTags] = useState(false);
    const [openModal, setOpenModal] = useState(false);

    const isMounted = useRef(true);

    useEffect(() => {
        return () => {
            isMounted.current = false
        }
    }, []);

    useEffect(() => {
        let mounted = true;
        const loadTagsAndSetState = async () => {
            try {
                await loadTags();
                if (mounted) {
                    if (Array.isArray(contact.tags)) {
                        setAllContactTags(contact.tags);
                        setSelecteds(contact.tags || []);
                    } else {
                        setAllContactTags([]);
                        setSelecteds([]);
                    }
                }
            } catch (err) {
                if (mounted) {
                    toastError(err);
                }
            }
        };

        if (isMounted.current) {
            loadTagsAndSetState();
        }

        return () => {
            mounted = false;
        };
    }, [contact]);

    const createTag = async (data) => {
        try {
            const { data: responseData } = await api.post(`/tags`, data);
            return responseData;
        } catch (err) {
            toastError(err);
        }
    }

    const loadTags = async () => {
        try {
            const { data } = await api.get(`/tags/list`,
                {
                    params: { kanban: 0 }
                });

            if (!isMounted.current) return;

            const filteredTags = data.filter((tag) => {
                return tag.userId === user.id || tag.isGlobal;
            });

            setTags(data && user.profile === 'user' ? filteredTags : data);
        } catch (err) {
            if (isMounted.current) {
                toastError(err);
            }
        };
    }

    const syncTags = async (data) => {
        try {
            const { data: responseData } = await api.post(`/tags/sync`, data);
            return responseData;
        } catch (err) {
            toastError(err);
        }
    }

    const onChange = async (value, reason) => {
        if (!isMounted.current) return;

        let optionsChanged = []
        if (reason === 'create-option') {
            if (user.profile !== 'admin') {
                toastError("Apenas administradores podem criar novas tags!");
                return;
            }
            if (isArray(value)) {
                for (let item of value) {
                    if (item.length < 3) {
                        toastError("Tag muito curta!");
                        return;
                    }
                    if (isString(item)) {
                        const newTag = await createTag({ name: item, kanban: 0, color: getRandomHexColor() })
                        if (!isMounted.current) return;
                        optionsChanged.push(newTag);
                    } else {
                        optionsChanged.push(item);
                    }
                }
            }
            if (isMounted.current) {
                await loadTags();
            }
        } else {
            optionsChanged = value;
        }

        if (!isMounted.current) return;

        setSelecteds(optionsChanged);

        let tagsToSync;

        // Como o backend agora gerencia as tags por usuário,
        // apenas enviamos as tags selecionadas pelo usuário atual
        tagsToSync = optionsChanged;

        setAllContactTags(tagsToSync);

        if (isMounted.current) {
            await syncTags({ contactId: contact.id, tags: tagsToSync });
        }
    }

    function getRandomHexColor() {

        const red = Math.floor(Math.random() * 256);
        const green = Math.floor(Math.random() * 256);
        const blue = Math.floor(Math.random() * 256);
        const hexColor = `#${red.toString(16).padStart(2, '0')}${green.toString(16).padStart(2, '0')}${blue.toString(16).padStart(2, '0')}`;

        return hexColor;
    }

    const hiddenTagsCount = selecteds.length - 2;

    const handleOpenModal = () => {
        setOpenModal(true);
    };

    const handleCloseModal = () => {
        setOpenModal(false);
    };

    const handleDeleteTag = async (tagToDelete) => {
        const newSelecteds = selecteds.filter(tag => tag.id !== tagToDelete.id);
        setSelecteds(newSelecteds);
        setAllContactTags(newSelecteds);
        
        try {
            await syncTags({ contactId: contact.id, tags: newSelecteds });
        } catch (err) {
            toastError(err);
        }
    };

    const isTagSelected = (tag) => {
        return selecteds.some(selected => selected.id === tag.id);
    };

    const darkenColor = (hexColor, factor) => {
        // Função para escurecer uma cor hexadecimal
        const hex = hexColor.replace('#', '');
        const r = Math.max(0, parseInt(hex.substr(0, 2), 16) - Math.round(255 * factor));
        const g = Math.max(0, parseInt(hex.substr(2, 2), 16) - Math.round(255 * factor));
        const b = Math.max(0, parseInt(hex.substr(4, 2), 16) - Math.round(255 * factor));
        return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
    };

    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Paper style={{ padding: '4px', width: '100%', maxWidth: 520 }}>
                <Autocomplete
                    multiple
                    size="small"
                    options={tags}
                    value={selecteds}
                    freeSolo
                    onChange={(e, v, r) => onChange(v, r)}
                    getOptionLabel={(option) => option.name}
                renderOption={(option, state) => {
                    const isSelected = isTagSelected(option);
                    const { selected, ...optionProps } = option.props || {};
                    
                    return (
                        <div 
                            {...optionProps}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px',
                                padding: '4px 6px',
                                margin: 0,
                                cursor: 'pointer',
                                ...optionProps.style
                            }}
                        >
                            <Chip
                                variant="outlined"
                                style={{
                                    backgroundColor: option.color || '#eee',
                                    color: '#FFF',
                                    fontWeight: 600,
                                    paddingLeft: 4,
                                    paddingRight: 4,
                                    borderRadius: 4,
                                    fontSize: '0.75em',
                                    whiteSpace: 'nowrap',
                                    opacity: isSelected ? 0.85 : 1
                                }}
                                label={option.name}
                                size="small"
                            />
                        </div>
                    );
                }}
                    renderTags={(value, getTagProps) => {
                        return (
                            <>
                                {value.slice(0, 2).map((option, index) => (
                                    <Chip
                                        variant="outlined"
                                        style={{
                                            backgroundColor: option.color || '#eee',
                                            color: "#FFF",
                                            marginRight: 1,
                                            padding: 1,
                                            fontWeight: 600,
                                            paddingLeft: 6,
                                            paddingRight: 6,
                                            borderRadius: 4,
                                            fontSize: "0.8em",
                                            whiteSpace: "nowrap"
                                        }}
                                        label={option.name}
                                        {...getTagProps({ index })}
                                        size="small"
                                    />
                                ))}
                            </>
                        );
                    }}
                    renderInput={(params) => (
                        <TextField {...params} variant="outlined" placeholder="Tags" />
                    )}
                    PaperComponent={({ children }) => (
                    <Paper style={{ marginLeft: 6, padding: '4px' }}>{children}</Paper>
                )}
                />
            </Paper>
            {selecteds.length > 2 && (
                <Button
                    size="small"
                    onClick={handleOpenModal}
                    style={{
                        minWidth: 'auto',
                        padding: '4px 8px',
                        fontSize: '0.75rem'
                    }}
                >
                    +{hiddenTagsCount} ver mais
                </Button>
            )}
            
            <Dialog
                open={openModal}
                onClose={handleCloseModal}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle style={{ padding: '16px', textAlign: 'center' }}>
                    Tags do Ticket
                    <IconButton
                        aria-label="close"
                        onClick={handleCloseModal}
                        style={{
                            position: 'absolute',
                            right: 8,
                            top: 8,
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent style={{ padding: '16px', minHeight: '100px' }}>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', justifyContent: 'center' }}>
                        {selecteds.map((tag, index) => (
                            <Chip
                                key={index}
                                variant="outlined"
                                style={{
                                    backgroundColor: tag.color || '#eee',
                                    color: "#FFF",
                                    fontWeight: 600,
                                    paddingLeft: 6,
                                    paddingRight: 6,
                                    borderRadius: 4,
                                    fontSize: "0.8em",
                                    whiteSpace: "nowrap"
                                }}
                                label={tag.name}
                                size="small"
                                onDelete={() => handleDeleteTag(tag)}
                            />
                        ))}
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    )
}
